import * as XLSX from 'xlsx';
import { writeExcelFile } from '../../utils/excelExport';
import { getIndicatorName as getIndicatorDisplayName } from '../../utils/indicatorName';

const CarryExportService = (fullCarriedData, countriesData, indicatorsData, unitsData, regionMappingsData) => {

    const exportDetailsToExcel = () => {

        if (!Array.isArray(fullCarriedData) || fullCarriedData.length === 0) {
            return;
        }

        const workbook = XLSX.utils.book_new();

        const groupedData = {};

        fullCarriedData.forEach((item) => {
            const key = `${item.r_code}-${item.ind_code}`;
            if (!groupedData[key]) {
                groupedData[key] = {
                    r_name: regionMappingsData.find(r => r.id === item.r_code)?.region_name || item.r_code,
                    r_code: item.r_code,
                    ind_code: item.ind_code,
                    u_code: item.u_code,
                    all_full_data: [],
                    years_data: {},
                    reference_years_display: item.reference_years_display || null,
                    seen_full_data: new Set()
                };
            }

            const fullData = item.full_data || {};
            const fullDataSignature = JSON.stringify({
                region_id: fullData.region_id,
                indicator_id: fullData.indicator_id,
                region_value: fullData.region_value || fullData.aggregated_result_dict,
                base_year: fullData.base_year || fullData.base_years,
                years: fullData.years,
                reference_year: fullData.reference_year || fullData.reference_years,
                countries_covered: fullData.countries_covered,
                aggregate_method: fullData.aggregate_method,
                weighted_indicator: fullData.weighted_indicator
            });

            if (!groupedData[key].seen_full_data.has(fullDataSignature)) {
                groupedData[key].seen_full_data.add(fullDataSignature);
                groupedData[key].all_full_data.push(fullData);
            }

            const yearKey = item.year ?? (Array.isArray(item.years) ? item.years[0] : null);
            if (yearKey !== null && yearKey !== undefined) {
                groupedData[key].years_data[yearKey] = {
                    region_result: item.region_result,
                    year: yearKey
                };
            }
        });
        Object.values(groupedData).forEach((groupedItem, index) => {
            const worksheetData = convertToWorksheetData(groupedItem);
            const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

            worksheet['!cols'] = [
                { width: 20 },
                { width: 25 },
                { width: 15 },
                { width: 30 },
                { width: 80 },
                { width: 15 },
                { width: 15 },
                { width: 25 },
                { width: 10 }
            ];
            let ind_code_length = String(groupedItem.ind_code).length;
            const sheetName = `${groupedItem.r_name.substring(0, (31 - ind_code_length - 1))}-${groupedItem.ind_code}`;
            XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
        });

        const filename = `carry-forward-data-${new Date().toISOString().split('T')[0]}.xlsx`;
        writeExcelFile(workbook, filename);
    };

    const getCountryName = (countryCode) => {
        const country = Object.values(countriesData || {}).find(c => c.c_code == countryCode);
        return country ? country.c_short_name_eng : countryCode;
    };

    const getIndicatorName = (indicatorCode) => {
        const indicator = Object.values(indicatorsData || {}).find(ind => ind.ind_code == indicatorCode);
        return indicator ? getIndicatorDisplayName(indicator) : indicatorCode;
    };

    const getUnitName = (unitCode) => {
        const unit = Object.values(unitsData || {}).find(u => u.unit_code == unitCode);
        return unit ? unit.unit_name_eng : unitCode;
    };

    const convertToWorksheetData = (groupedItem) => {
        const data = [];
        const mainData = groupedItem.all_full_data[0] || {};

        data.push(['SUMMARY INFORMATION']);
        data.push(['Region Code', groupedItem.r_code || '']);
        const region = regionMappingsData.find(r => r.id === groupedItem.r_code);
        data.push(['Region Name', region ? region.region_name : groupedItem.r_code]);
        data.push(['Indicator Code', groupedItem.ind_code || mainData.indicator_code || '']);
        data.push(['Indicator Name', getIndicatorName(groupedItem.ind_code || mainData.indicator_code)]);
        data.push(['Unit Code', groupedItem.u_code || mainData.unit || mainData.indicator_unit || '']);
        data.push(['Unit Name', getUnitName(groupedItem.u_code || mainData.unit || mainData.indicator_unit)]);
        data.push(['Countries Count', mainData.countries_covered || mainData.length || '']);
        data.push(['Aggregation Method', mainData.aggregate_method || '']);
        data.push(['Weighted Indicator', mainData.weighted_indicator || 'N/A']);

        const allBaseYears = Array.from(new Set(groupedItem.all_full_data.flatMap(fd => Array.isArray(fd.base_year) ? fd.base_year : (Array.isArray(fd.base_years) ? fd.base_years : [])))).sort();
        data.push(['Base Year', allBaseYears.length > 0 ? allBaseYears.join(', ') : 'N/A']);

        const allRefYears = Array.from(new Set(groupedItem.all_full_data.flatMap(fd => Array.isArray(fd.reference_year) ? fd.reference_year : (Array.isArray(fd.reference_years) ? fd.reference_years : [])))).sort();
        const refDisplay = groupedItem.reference_years_display;
        data.push(['Reference Year', refDisplay && refDisplay !== 'N/A' ? refDisplay : (allRefYears.length > 0 ? allRefYears.join(', ') : 'N/A')]);
        data.push([]);

        data.push(['YEAR RESULTS']);
        data.push(['Year', 'Aggregated Result']);

        Object.entries(groupedItem.years_data).sort((a, b) => a[0] - b[0]).forEach(([year, yearData]) => {
            data.push([year, yearData.region_result]);
        });
        data.push([]);

        groupedItem.all_full_data.forEach((fullDataItem) => {
            if (fullDataItem.base_indicator_values_list && Array.isArray(fullDataItem.base_indicator_values_list)) {
                fullDataItem.base_indicator_values_list.forEach((yearData, yearIndex) => {
                    const year = fullDataItem.years?.[yearIndex] || `Year ${yearIndex + 1}`;

                    data.push([`BASE INDICATOR VALUES - ${year}`]);
                    data.push(['Country Code', 'Country Name', 'Base Year', 'Indicator Code', 'Indicator Name', 'Value', 'Date', 'Unit Name', 'Data Year']);

                    Object.entries(yearData).forEach(([countryCode, dataInfo]) => {
                        const dataObj = dataInfo.data_object || {};
                        data.push([
                            countryCode,
                            getCountryName(countryCode),
                            dataInfo.base_year || '',
                            dataObj.ind_code || '',
                            getIndicatorName(dataObj.ind_code),
                            dataObj.d_value || '',
                            dataObj.d_date || '',
                            getUnitName(dataObj.u_code),
                            dataObj.d_year || ''
                        ]);
                    });
                    data.push([]);
                });
            }

            if (fullDataItem.weighted_indicator_values_list && Array.isArray(fullDataItem.weighted_indicator_values_list)) {
                fullDataItem.weighted_indicator_values_list.forEach((yearData, yearIndex) => {
                    const year = fullDataItem.years?.[yearIndex] || `Year ${yearIndex + 1}`;

                    data.push([`WEIGHTED INDICATOR VALUES - ${year}`]);
                    data.push(['Country Code', 'Country Name', 'Base Year', 'Indicator Code', 'Indicator Name', 'Value', 'Date', 'Unit Name', 'Data Year']);

                    Object.entries(yearData).forEach(([countryCode, dataInfo]) => {
                        const dataObj = dataInfo.data_object || {};
                        data.push([
                            countryCode,
                            getCountryName(countryCode),
                            dataInfo.base_year || '',
                            dataObj.ind_code || '',
                            getIndicatorName(dataObj.ind_code),
                            dataObj.d_value || '',
                            dataObj.d_date || '',
                            getUnitName(dataObj.u_code),
                            dataObj.d_year || ''
                        ]);
                    });
                    data.push([]);
                });
            }

        });

        return data;
    };

    const exportTableResults = () => {
        if (!Array.isArray(fullCarriedData) || fullCarriedData.length === 0) {
            console.error('No data to export');
            return;
        }

        const exportData = fullCarriedData.map(item => {
            const region = regionMappingsData.find(r => r.id === item.r_code);
            const indicator = Object.values(indicatorsData || {}).find(ind => ind.ind_code === item.ind_code);
            const weightedInd = item.weighted_indicator ? Object.values(indicatorsData || {}).find(ind => ind.ind_code === item.weighted_indicator) : null;

            return {
                'Region Name': region ? region.region_name : item.r_code,
                'Indicator Code': item.ind_code,
                'Indicator Name': indicator ? getIndicatorDisplayName(indicator) : item.ind_code,
                'Unit Name': getUnitName(item.u_code),
                'Aggregate Method': item.aggregate_method || 'N/A',
                'Weighted Indicator': item.weighted_indicator ? (weightedInd ? `${item.weighted_indicator} - ${getIndicatorDisplayName(weightedInd)}` : item.weighted_indicator) : 'N/A',
                'Base Years': Array.isArray(item.base_year) && item.base_year.length > 0 ? item.base_year.join(', ') : 'N/A',
                'Reference Years': item.reference_years_display || 'N/A',
                'Aggregated Result': typeof item.region_result === 'number' ? item.region_result.toFixed(6) : item.region_result,
                'Countries Count': item.countries_covered || 0,
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(exportData);

        worksheet['!cols'] = [
            { width: 25 },
            { width: 15 },
            { width: 80 },
            { width: 20 },
            { width: 15 },
            { width: 20 },
            { width: 60 },
            { width: 20 },
            { width: 25 },
            { width: 20 },
            { width: 15 }
        ];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Carry Results');

        const filename = `carry-results-${new Date().toISOString().split('T')[0]}.xlsx`;
        writeExcelFile(workbook, filename);
    };

    return { exportTableResults, exportDetailsToExcel };
};

export default CarryExportService;